import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import type { PlanGenerateMode } from './types';

export interface GenerateCounts {
  /** All filled cells (shift or day off). */
  assigned: number;
  pinned: number;
  unpinned: number;
  /** Cells of people with a zmianowość that are still empty. */
  empty: number;
  /** People the algorithm can plan (have a zmianowość). */
  plannablePeople: number;
}

interface GeneratePlanDialogProps {
  open: boolean;
  counts: GenerateCounts;
  busy: boolean;
  onGenerate: (mode: PlanGenerateMode) => void;
  onClose: () => void;
}

const MODES: Array<{ mode: PlanGenerateMode; title: string }> = [
  { mode: 'FILL_EMPTY', title: 'Uzupełnij puste komórki' },
  { mode: 'REGENERATE_UNPINNED', title: 'Ułóż od nowa (bez przypiętych)' },
];

export const GeneratePlanDialog: React.FC<GeneratePlanDialogProps> = ({
  open,
  counts,
  busy,
  onGenerate,
  onClose,
}) => {
  const [mode, setMode] = useState<PlanGenerateMode>('FILL_EMPTY');
  useEffect(() => {
    if (open) setMode('FILL_EMPTY');
  }, [open]);

  const emptyPlan = counts.assigned === 0;

  const describe = (m: PlanGenerateMode) =>
    m === 'FILL_EMPTY' ? (
      <>
        Program zaplanuje tylko puste komórki ({counts.empty}). Wszystko, co już jest w planie ({counts.assigned}),
        zostanie bez zmian.
      </>
    ) : (
      <>
        <strong>{counts.unpinned}</strong> nieprzypiętych komórek zostanie zastąpionych nowym układem.{' '}
        <strong>{counts.pinned}</strong> przypiętych komórek (zmienionych ręcznie albo z importu) zostanie bez zmian.
      </>
    );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent className="sm:max-w-[540px]" data-testid="plan-generate-dialog">
        <DialogHeader>
          <DialogTitle>Ułóż plan automatycznie</DialogTitle>
          <DialogDescription>
            Program rozdzieli zmiany tak, aby spełnić wymaganą obsadę, nieobecności i wyjątki. Planuje tylko osoby z
            przypisaną zmianowością ({counts.plannablePeople}). Dni nieobecności zostają puste.
          </DialogDescription>
        </DialogHeader>

        {emptyPlan ? (
          <p className="text-sm">Plan jest jeszcze pusty — program ułoży cały tydzień.</p>
        ) : (
          <div className="space-y-2" role="radiogroup" aria-label="Sposób układania">
            {MODES.map(({ mode: m, title }) => (
              <label
                key={m}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 text-sm transition-colors',
                  mode === m ? 'border-primary ring-2 ring-primary bg-primary/5' : 'border-input hover:bg-muted',
                )}
              >
                <input
                  type="radio"
                  name="plan-generate-mode"
                  value={m}
                  checked={mode === m}
                  onChange={() => setMode(m)}
                  className="mt-1 h-4 w-4 accent-[#168dc8]"
                  data-testid={`plan-generate-mode-${m}`}
                />
                <span>
                  <span className="block font-medium">{title}</span>
                  <span className="block text-muted-foreground">{describe(m)}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Anuluj
          </Button>
          <Button
            onClick={() => onGenerate(emptyPlan ? 'FILL_EMPTY' : mode)}
            disabled={busy}
            data-testid="plan-generate-run"
          >
            {busy ? 'Układanie...' : 'Ułóż plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
